import React, { useState } from 'react';
import { PipelineEventEnvelope, StepLogPayload } from './interfaces';
import './theme.css';

const LEVELS: StepLogPayload['level'][] = ['debug', 'info', 'warning', 'error'];

/** Default visible levels -- debug hidden by default per explicit user direction to capture everything but keep the live view usable. */
const DEFAULT_VISIBLE_LEVELS: Set<StepLogPayload['level']> = new Set(['info', 'warning', 'error']);

/**
 * Static, honest placeholder lines shown for a step while it's actively
 * displayed -- e.g. a Processing job (preprocess/evaluate) still
 * provisioning its container, before any script code (and therefore any
 * real emit_log call) has run. Unlike Training jobs, Processing jobs have
 * no SecondaryStatus sub-state at all (confirmed live -- see
 * specs/open/smartwater-granular-event-history.md's Addendum), so there
 * is no real signal to surface during that wait. This is NOT fabricated
 * progress data -- it never claims a specific action is happening right
 * now, only describes what the step is generally responsible for.
 * Deliberately left visible even after real step_log events start
 * arriving (not hidden once the first real line lands) -- per explicit
 * user direction, avoiding a jarring disappear-on-first-event flicker.
 * Two lines per step, matching the "at least one or two" ask.
 */
const STARTUP_PLACEHOLDER_MESSAGES: Record<string, string[]> = {
  preprocess: [
    'Provisioning compute for data preparation.',
    'Will read raw input data, drop non-feature columns, and split into train/test/full-train sets once running.',
  ],
  train: [
    'Provisioning compute for model training.',
    'Will train a LightGBM regressor with early stopping against a held-out validation split once running.',
  ],
  evaluate: [
    'Provisioning compute for model evaluation.',
    'Will score the trained model against the held-out test set and compute regression metrics once running.',
  ],
  full_train: [
    'Provisioning compute for final model training.',
    'Will retrain on the full dataset (train + validation combined) once running.',
  ],
  create_model: ['Registering the trained model artifact as a SageMaker Model.'],
  register: ['Registering the model package in the model registry.'],
};

interface StepLogPanelProps {
  /** The currently-active/selected step's log lines, in arrival order -- see Dashboard.tsx's stepLogs state. */
  logs: PipelineEventEnvelope[];
  /** The step_role this panel is showing, for the header label. */
  stepRole: string;
}

/**
 * Streaming log panel for one step: a level-filter toggle (debug hidden by
 * default) over a scrolling list of step_log lines, each showing its
 * event_id, level, and message -- the "sub-section of the visualization
 * that is a streaming view of what's going on inside the system" the user
 * asked for. See specs/open/smartwater-granular-event-history.md's
 * Approach item 3 and 5.
 *
 * Every event_id is displayed, not just carried in the data -- per that
 * spec's frontend section: event_id already existed on every event, but
 * no view rendered it per-event before this panel.
 */
export function StepLogPanel({ logs, stepRole }: StepLogPanelProps) {
  const [visibleLevels, setVisibleLevels] = useState<Set<StepLogPayload['level']>>(
    new Set(DEFAULT_VISIBLE_LEVELS),
  );

  function toggleLevel(level: StepLogPayload['level']): void {
    setVisibleLevels((previous) => {
      const next = new Set(previous);
      if (next.has(level)) {
        next.delete(level);
      } else {
        next.add(level);
      }
      return next;
    });
  }

  const visibleLogs = logs.filter((event) => visibleLevels.has((event.payload as StepLogPayload).level));
  const placeholderMessages = STARTUP_PLACEHOLDER_MESSAGES[stepRole] ?? [];

  return (
    <div className="scorecard" data-testid={`step-log-panel-${stepRole}`}>
      <div className="title">{stepRole} activity</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        {LEVELS.map((level) => (
          <label key={level} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={visibleLevels.has(level)}
              onChange={() => toggleLevel(level)}
              data-testid={`step-log-level-toggle-${level}`}
            />
            {level}
          </label>
        ))}
      </div>
      {placeholderMessages.length > 0 && (
        <div
          data-testid={`step-log-placeholder-${stepRole}`}
          style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 8 }}
        >
          {placeholderMessages.map((message, index) => (
            <div key={index} style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--text-dim)' }}>
              {message}
            </div>
          ))}
        </div>
      )}
      {visibleLogs.length === 0 ? (
        <p style={{ color: 'var(--text-dim)', fontSize: 12 }}>No log lines at the selected levels yet.</p>
      ) : (
        <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {visibleLogs.map((event) => {
            const payload = event.payload as StepLogPayload;
            return (
              <div
                key={event.event_id}
                data-testid={`step-log-line-${event.event_id}`}
                style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text-dim)' }}
              >
                <span style={{ opacity: 0.6 }}>{event.timestamp}</span>{' '}
                <span style={{ textTransform: 'uppercase', opacity: 0.6 }}>[{payload.level}]</span>{' '}
                <span style={{ color: 'var(--text)' }}>{payload.message}</span>{' '}
                <span style={{ opacity: 0.4 }} title="event_id">
                  ({event.event_id})
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
